/**
 * CSV helpers shared by the CSV-to-table, CSV-to-JSON and Markdown table tools: an
 * RFC 4180 reader (quoted fields may hold delimiters, doubled quotes and line breaks;
 * CRLF, LF and CR all end a row) and the delimiter choice each tool offers.
 */
import { detectDelimiter, parseDelimited } from "@/lib/tool-kit/text/delimited";

export type CsvDelimiterChoice = "auto" | "comma" | "semicolon" | "tab";
export type CsvDelimiterName = Exclude<CsvDelimiterChoice, "auto">;

export const CSV_DELIMITER_CHARS: Record<CsvDelimiterName, string> = {
  comma: ",",
  semicolon: ";",
  tab: "\t",
};

const CHARS = Object.values(CSV_DELIMITER_CHARS);

/** Parse CSV text into rows of fields, dropping rows that are completely empty. */
export function parseCsvRows(text: string, delimiter: string): string[][] {
  return parseDelimited(text, delimiter).rows.filter(
    (row) => !(row.length === 1 && row[0].trim() === "")
  );
}

/** The delimiter the text most likely uses: comma, semicolon or tab, quotes respected. */
export function detectCsvDelimiter(text: string): string {
  return detectDelimiter(text, CHARS);
}

/** Name of a delimiter character, so a detected one can be shown with its label. */
export function csvDelimiterName(char: string): CsvDelimiterName {
  if (char === "\t") {
    return "tab";
  }
  return char === ";" ? "semicolon" : "comma";
}

/** The character to split CSV text on for the chosen option. */
export function resolveCsvDelimiter(
  choice: CsvDelimiterChoice,
  text: string
): string {
  return choice === "auto"
    ? detectCsvDelimiter(text)
    : CSV_DELIMITER_CHARS[choice];
}

/**
 * The character to write JSON → CSV with. There is no CSV to inspect in that
 * direction, so "auto" means the default comma — never a guess from the JSON text.
 */
export function resolveJsonToCsvDelimiter(choice: CsvDelimiterChoice): string {
  return choice === "auto" ? "," : CSV_DELIMITER_CHARS[choice];
}

const CELL_LINE_BREAK = /\r\n|\r|\n/g;

/**
 * Pasted CSV or TSV as table cells for Markdown, which has no multi-line cells: a line
 * break inside a quoted field becomes `<br>`, and cells are trimmed.
 */
export function parsePastedTable(text: string): string[][] {
  return parseCsvRows(text, detectCsvDelimiter(text)).map((row) =>
    row.map((cell) => cell.trim().replace(CELL_LINE_BREAK, "<br>"))
  );
}
